import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { getIdToken } from "@app/services/auth-storage";
import { AuthService } from '@app/services/http/auth.service';
import { TimeoutCheckService } from '@app/services/timeout-check.service';
import { Common } from '@app/utils/common';
import * as CryptoJS from 'crypto-js';
import { throwError, TimeoutError } from "rxjs";
import { catchError, timeout } from 'rxjs/operators';
import { environment } from './environment';

function createWarningIcon(): HTMLElement {
  const iconDiv = document.createElement('div');
  const iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  iconSvg.setAttribute('width', '36');
  iconSvg.setAttribute('height', '36');
  iconSvg.setAttribute('viewBox', '0 0 36 36');
  iconSvg.setAttribute('fill', 'none');

  Object.assign(iconSvg.style, {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    fontSize: '40px',
    color: '#ffaf00'
  });

  const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  circle.setAttribute('cx', '18');
  circle.setAttribute('cy', '18');
  circle.setAttribute('r', '18');
  circle.setAttribute('fill', 'black');

  const warning_path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  warning_path.setAttribute(
    'd',
    'M18 27C18.51 27 18.9375 26.8275 19.2825 26.4825C19.6275 26.1375 19.8 25.71 19.8 25.2C19.8 24.69 19.6275 24.2625 19.2825 23.9175C18.9375 23.5725 18.51 23.4 18 23.4C17.49 23.4 17.0625 23.5725 16.7175 23.9175C16.3725 24.2625 16.2 24.69 16.2 25.2C16.2 25.71 16.3725 26.1375 16.7175 26.4825C17.0625 26.8275 17.49 27 18 27ZM16.2 19.8H19.8V9H16.2V19.8ZM18 36C15.51 36 13.17 35.5275 10.98 34.5825C8.79 33.6375 6.885 32.355 5.265 30.735C3.645 29.115 2.3625 27.21 1.4175 25.02C0.4725 22.83 0 20.49 0 18C0 15.51 0.4725 13.17 1.4175 10.98C2.3625 8.79 3.645 6.885 5.265 5.265C6.885 3.645 8.79 2.3625 10.98 1.4175C13.17 0.4725 15.51 0 18 0C20.49 0 22.83 0.4725 25.02 1.4175C27.21 2.3625 29.115 3.645 30.735 5.265C32.355 6.885 33.6375 8.79 34.5825 10.98C35.5275 13.17 36 15.51 36 18C36 20.49 35.5275 22.83 34.5825 25.02C33.6375 27.21 32.355 29.115 30.735 30.735C29.115 32.355 27.21 33.6375 25.02 34.5825C22.83 35.5275 20.49 36 18 36Z'
  );
  warning_path.setAttribute('fill', '#FFB000');

  iconSvg.appendChild(circle);
  iconSvg.appendChild(warning_path);
  iconDiv.appendChild(iconSvg);
  return iconDiv;
}

function createTitleContainer(): HTMLElement {
  const titleContainer = document.createElement('div');
  titleContainer.style.cssText = `
    display: flex;
    align-items: center;
    margin-top: 10px;`;

  const iconDiv = createWarningIcon();

  const titleDiv = document.createElement('div');
  titleDiv.style.cssText = `
      font-size: 20px;
      font-weight: 600;
      margin-left: 15px;`;
  titleDiv.textContent = 'Session Terminated';

  titleContainer.appendChild(iconDiv);
  titleContainer.appendChild(titleDiv);

  return titleContainer;
}

function createMessageDiv(): HTMLElement {
  const messageDiv = document.createElement('div');
  messageDiv.style.cssText = `
      margin-left: 55px;
      white-space: pre-line;`;
  messageDiv.textContent = 'Your session has been terminated because your account was accessed from another device or browser.\n You have been logged out. Please click OK to return to the login page and sign in again.';

  return messageDiv;
}

function createLogoutButton(): HTMLElement {
  const logoutBtn = document.createElement('button');
  logoutBtn.className = 'rect green-btn';
  logoutBtn.style.cssText = `
      color: white;
      background-color: #009e73;
      border-radius: 4px;
      border: none;
      padding: 8px 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      margin: 0 12px;
    `;

  const logoutContent = document.createElement('div');
  logoutContent.style.display = 'flex';
  logoutContent.style.alignItems = 'center';
  logoutContent.appendChild(document.createTextNode('OK'));
  logoutBtn.appendChild(logoutContent);

  return logoutBtn;
}

function createButtonRow(logoutBtn: HTMLElement): HTMLElement {
  const buttonRow = document.createElement('div');
  buttonRow.style.display = 'flex';
  buttonRow.style.justifyContent = 'center';
  buttonRow.style.marginTop = '16px';
  buttonRow.appendChild(logoutBtn);

  return buttonRow;
}

function createModal(toast: HTMLElement): HTMLElement {
  const modal = document.createElement('div');
  modal.style.position = 'fixed';
  modal.style.top = '0';
  modal.style.left = '0';
  modal.style.width = '100%';
  modal.style.height = '100%';
  modal.style.backgroundColor = 'rgba(0,0,0,0.5)';
  modal.style.display = 'flex';
  modal.style.justifyContent = 'center';
  modal.style.alignItems = 'center';
  modal.style.zIndex = '10000';
  modal.appendChild(toast);

  return modal;
}

function createToast(): HTMLElement {
  const toast = document.createElement('div');
  toast.style.cssText = `
    display: block;
    align-items: center;
    border-radius: 4px;
    justify-content: space-between;
    min-width: 400px;
    min-height: 110px;
    color: #000;
    padding: 10px 30px;
    background: #fff0cf;`;

  return toast;
}

function createSessionTerminatedModal(): { modal: HTMLElement; logoutBtn: HTMLElement } {
  const toast = createToast();
  const titleContainer = createTitleContainer();
  const messageDiv = createMessageDiv();
  const logoutBtn = createLogoutButton();
  const buttonRow = createButtonRow(logoutBtn);
  const modal = createModal(toast);

  toast.appendChild(titleContainer);
  toast.appendChild(messageDiv);
  toast.appendChild(buttonRow);

  return { modal, logoutBtn };
}


function performLogout(
  modal: HTMLElement,
  timeoutCheck: TimeoutCheckService,
  authService: AuthService,
  router: Router
): void {
  modal.remove();

  setTimeout(() => {
    timeoutCheck.stopTimer();
    authService.UserSessionLogger("logout");
    Common.logout(router);
  }, 0);
}

function handleSessionTerminated(
  timeoutCheck: TimeoutCheckService,
  authService: AuthService,
  router: Router
): void {
  const { modal, logoutBtn } = createSessionTerminatedModal();
  document.body.appendChild(modal);

  logoutBtn.addEventListener('click', () => {
    performLogout(modal, timeoutCheck, authService, router);
  });
}

function handleTimeoutError(error: TimeoutError): void {
  console.error('Request timeout, please try again later', error.message);
}

function handleNotFoundError(req: HttpRequest<unknown>, error: HttpErrorResponse): void {
  console.error(`API ${req.url} is not found`, error.error.message);
}

function handleSessionTerminatedError(
  timeoutCheck: TimeoutCheckService,
  authService: AuthService,
  router: Router
): void {
  handleSessionTerminated(timeoutCheck, authService, router);
}

function handleClientError(error: HttpErrorResponse): void {
  console.error('An error occurred:', error.error.message);
}

function handleServerError(error: HttpErrorResponse): void {
  console.error(`Backend returned code ${error.status}, body was: ${error.error}`);
}

function handleHttpError(
  error: HttpErrorResponse,
  req: HttpRequest<unknown>,
  timeoutCheck: TimeoutCheckService,
  authService: AuthService,
  router: Router
): void {
  console.error(error);

  if (error instanceof TimeoutError) {
    handleTimeoutError(error);
  } else if (error.status === 404) {
    handleNotFoundError(req, error);
  } else if (error.status === 403) {
    handleSessionTerminatedError(timeoutCheck, authService, router);
  } else if (error.error instanceof ErrorEvent) {
    handleClientError(error);
  } else {
    handleServerError(error);
  }
}

export const authenticationInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next:
  HttpHandlerFn) => {
  const idToken = getIdToken();
  const router = inject(Router);
  const timeoutCheck = inject(TimeoutCheckService);
  const authService = inject(AuthService);

  if (idToken) {
    req = req.clone({ headers: req.headers.set('Authorization', idToken) });
  }

  // Seal the write operation requests that contain a body.
  // Skip when there is no idToken (samlRequest / checkLogin are sent before login): the seal key is the
  // idToken, and CryptoJS.HmacSHA256(payload, null) throws TypeError, which would abort the request.
  if (environment.requestSealingEnabled
    && idToken
    && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(req.method)
    && req.body) {

    const timestamp = Date.now().toString();
    const nonce = CryptoJS.lib.WordArray.random(16).toString();

    // Get the API path from the request URL, handling any potential errors in URL parsing
    let apiPath: string;
    try {
      apiPath = new URL(req.urlWithParams).pathname;
    } catch {
      apiPath = req.urlWithParams;
    }

    const payload = [
      req.method,
      apiPath,
      JSON.stringify(req.body ?? ''),
      timestamp,
      nonce
    ].join('\n');

    const seal = CryptoJS.HmacSHA256(payload, idToken!)
      .toString(CryptoJS.enc.Hex);

    // Only add headers, do not modify the body
    req = req.clone({
      headers: req.headers
        .set('X-Seal', seal)
        .set('X-Timestamp', timestamp)
        .set('X-Nonce', nonce)
    });
  }

  return next(req).pipe(
    timeout(15000),
    catchError((error: HttpErrorResponse) => {
      handleHttpError(error, req, timeoutCheck, authService, router);
      return throwError(() => error);
    })
  );
};

